const POLYMARKET_BASE_URL = 'https://gamma-api.polymarket.com'

// Raw API response types
export type PolymarketTag = {
	id: string
	label: string
	slug: string
}

export type PolymarketMarket = {
	id: string
	question: string
	conditionId: string
	slug: string
	resolutionSource: string
	endDate: string
	liquidity: string
	startDate: string
	image: string
	icon: string
	description: string
	outcomes: string // JSON string like '["Yes", "No"]'
	outcomePrices: string // JSON string like '["0.55", "0.45"]'
	volume: string
	events?: PolymarketEvent[]
	// Polymarket market status flags:
	// - active: Market is open for trading
	// - closed: Market is closed/resolved
	// - archived: Market is hidden from main feeds (old or removed)
	// - new: Market is recently created/featured
	active: boolean
	closed: boolean
	archived?: boolean
	new?: boolean
	marketMakerAddress: string
	volumeNum: number
	liquidityNum?: number
	clobTokenIds: string // JSON string
	bestBid?: number
	bestAsk?: number
	lastTradePrice?: number
	oneDayPriceChange?: number
	oneWeekPriceChange?: number
	groupItemTitle?: string // e.g. "Atlanta" for Super Bowl teams
}

export type PolymarketEvent = {
	id: string
	ticker: string
	slug: string
	title: string
	description: string
	resolutionSource: string
	startDate: string
	endDate: string
	image: string
	icon: string
	active: boolean
	closed: boolean
	liquidity: number
	volume: number
	volume24hr?: number
	commentCount: number
	markets: PolymarketMarket[]
	tags: PolymarketTag[]
	negRisk?: boolean
	competitive?: number
}

export type PolymarketEventsResponse = PolymarketEvent[]

export type PolymarketSearchResponse = {
	events?: PolymarketEvent[] | null
	pagination?: {
		hasMore?: boolean
		totalResults?: number
	}
}

// Fetch events list
export async function fetchPolymarketEvents(params?: {
	limit?: number
	offset?: number
	order?: 'volume' | 'liquidity' | 'createdAt' | 'id'
	ascending?: boolean
	// Status booleans - all can be set independently
	active?: boolean
	closed?: boolean
	archived?: boolean
	tag?: string
}): Promise<PolymarketEventsResponse> {
	const searchParams = new URLSearchParams()

	searchParams.set('limit', (params?.limit ?? 50).toString())
	if (params?.offset) {
		searchParams.set('offset', params.offset.toString())
	}
	searchParams.set('order', params?.order ?? 'volume')
	searchParams.set('ascending', (params?.ascending ?? false).toString())
	// Status boolean filters
	// if (params?.active !== undefined) {
	// 	searchParams.set('active', params.active.toString())
	// }
	if (params?.closed !== undefined) {
		searchParams.set('closed', params.closed.toString())
	}
	// if (params?.archived !== undefined) {
	// 	searchParams.set('archived', params.archived.toString())
	// }
	if (params?.tag) {
		searchParams.set('tag_slug', params.tag)
	}

	const url = `${POLYMARKET_BASE_URL}/events?${searchParams.toString()}`
	const res = await fetch(url, { next: { revalidate: 30 } })

	if (!res.ok) {
		throw new Error(`Polymarket API error: ${res.status} ${res.statusText}`)
	}

	const body = await res.json()
	// Polymarket has two response shapes depending on endpoint:
	// - GET /events returns an array of events (body is Array)
	// - GET /public-search returns an object with `events` property
	// Support both shapes and return a stable array to callers.
	if (Array.isArray(body)) {
		return body as PolymarketEventsResponse
	}
	return body?.events ?? []
}

// Fetch single event by slug
export async function fetchPolymarketEvent(slug: string): Promise<PolymarketEvent> {
	const url = `${POLYMARKET_BASE_URL}/events?slug=${encodeURIComponent(slug)}`
	const res = await fetch(url, { next: { revalidate: 10 } })

	if (!res.ok) {
		throw new Error(`Polymarket API error: ${res.status} ${res.statusText}`)
	}

	const events = await res.json()
	if (!events || events.length === 0) {
		throw new Error(`Event not found: ${slug}`)
	}

	return events[0]
}

// Fetch single event by ID
export async function fetchPolymarketEventById(id: string): Promise<PolymarketEvent> {
	const url = `${POLYMARKET_BASE_URL}/events?id=${encodeURIComponent(id)}`
	const res = await fetch(url, { next: { revalidate: 10 } })

	if (!res.ok) {
		throw new Error(`Polymarket API error: ${res.status} ${res.statusText}`)
	}

	const events = await res.json()
	if (!events || events.length === 0) {
		throw new Error(`Event not found: ${id}`)
	}

	return events[0]
}

// Fetch markets directly (flat list, not grouped by event)
// Note: Use 'volumeNum'/'liquidityNum' for proper numeric sorting (string fields sort alphabetically)
export async function fetchPolymarketMarkets(params?: {
	limit?: number
	offset?: number
	order?: string // volumeNum, liquidityNum, volume24hr, createdAt, startDate, etc.
	ascending?: boolean
	// Status booleans - all can be set independently
	active?: boolean
	closed?: boolean
	archived?: boolean
}): Promise<PolymarketMarket[]> {
	const searchParams = new URLSearchParams()

	searchParams.set('limit', (params?.limit ?? 50).toString())
	if (params?.offset) {
		searchParams.set('offset', params.offset.toString())
	}
	searchParams.set('order', params?.order ?? 'volumeNum')
	searchParams.set('ascending', (params?.ascending ?? false).toString())
	// Status boolean filters
	// if (params?.active !== undefined) {
	// 	searchParams.set('active', params.active.toString())
	// }
	if (params?.closed !== undefined) {
		searchParams.set('closed', params.closed.toString())
	}
	// if (params?.archived !== undefined) {
	// 	searchParams.set('archived', params.archived.toString())
	// }

	const url = `${POLYMARKET_BASE_URL}/markets?${searchParams.toString()}`
	const res = await fetch(url, { next: { revalidate: 30 } })

	if (!res.ok) {
		throw new Error(`Polymarket API error: ${res.status} ${res.statusText}`)
	}

	return res.json()
}

// Helper to parse outcome prices
export function parseOutcomePrices(outcomePrices: string): [number, number] {
	try {
		const parsed = JSON.parse(outcomePrices)
		return [Number.parseFloat(parsed[0]) || 0, Number.parseFloat(parsed[1]) || 0]
	} catch {
		return [0.5, 0.5]
	}
}

// Helper to parse CLOB token IDs
export function parseClobTokenIds(clobTokenIds: string): [string, string] {
	try {
		const parsed = JSON.parse(clobTokenIds)
		return [parsed[0] || '', parsed[1] || '']
	} catch {
		return ['', '']
	}
}

// Search public endpoint (returns markets/events/profiles). We request only events here.
export async function fetchPolymarketSearch(
	query: string,
	params?: { limit?: number; offset?: number }
): Promise<PolymarketSearchResponse> {
	const searchParams = new URLSearchParams()

	// Polymarket expects `q` as the query parameter
	searchParams.set('q', query)

	const limit = params?.limit ?? 50
	// Use `limit_per_type` and page (1-indexed) per Polymarket API
	searchParams.set('limit_per_type', limit.toString())

	const offset = params?.offset ?? 0
	const page = Math.floor(offset / limit) + 1
	searchParams.set('page', page.toString())

	// Restrict results to events only
	searchParams.set('types', 'events')

	const url = `${POLYMARKET_BASE_URL}/public-search?${searchParams.toString()}`
	const res = await fetch(url, { next: { revalidate: 30 } })

	if (!res.ok) {
		throw new Error(`Polymarket API error: ${res.status} ${res.statusText}`)
	}

	const body: PolymarketSearchResponse = await res.json()
	return body
}
